import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { CentrexDeviceModel } from '../models/centrex-device.model';
import { ApiService } from './api.service';
import { ServiceBase } from './service-base';
import { EnreachUpdateDeviceModel } from '../models/enreach-update-device.model';

@Injectable({
  providedIn: 'root',
})
export class CentrexDevicesService extends ServiceBase {
  private devicesCache = new Map<string, Observable<CentrexDeviceModel[]>>();

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {
    super();
  }

  private createHeaders(additionalHeaders?: {
    [key: string]: string;
  }): HttpHeaders {
    const token = this.apiService.getAccessToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach((key) => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }

    return headers;
  }

  private handleError(error: any): Observable<never> {
    console.error('Erreur CentrexDevicesService', error);
    return throwError(() => error);
  }

  getDevices(
    organizationId: string,
    platform: string,
  ): Observable<CentrexDeviceModel[]> {
    const cacheKey = `${organizationId}_${platform}`;

    // retourne le cache si présent
    if (this.devicesCache.has(cacheKey)) {
      return this.devicesCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/api/centrex/devices`;

    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    const request$ = this.http
      .get<CentrexDeviceModel[]>(url, {
        headers,
      })
      .pipe(
        shareReplay(1),

        catchError((error) => {
          this.devicesCache.delete(cacheKey);
          return this.handleError(error);
        }),
      );

    this.devicesCache.set(cacheKey, request$);

    return request$;
  }

  getDeviceById(
    organizationId: string,
    platform: string,
    deviceId: string,
  ): Observable<CentrexDeviceModel> {
    const url = `${this.apiUrl}/api/centrex/devices/${deviceId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http
      .get<CentrexDeviceModel>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  createDevice(
    organizationId: string,
    platform: string,
    model: CentrexDeviceModel,
  ): Observable<void> {
    const url = `${this.apiUrl}/api/centrex/devices`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.post<void>(url, model, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.devicesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  updateDevice(
    organizationId: string,
    platform: string,
    deviceId: string,
    model: CentrexDeviceModel,
  ): Observable<void> {
    const url = `${this.apiUrl}/api/centrex/devices/${deviceId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    let payload: CentrexDeviceModel | EnreachUpdateDeviceModel = model;

    if (platform === 'Centrex Enreach') {
      payload = this.mapToEnreachUpdateDevice(deviceId, model);
    }

    return this.http.patch<void>(url, payload, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.devicesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  private mapToEnreachUpdateDevice(
    deviceId: string,
    device: CentrexDeviceModel,
  ): EnreachUpdateDeviceModel {
    return {
      Id: deviceId,
      Name: device.name,
      Address: device.address,
      SiteName: device.siteName,
    };
  }

  deleteDevice(
    organizationId: string,
    platform: string,
    deviceId: string,
  ): Observable<void> {
    const url = `${this.apiUrl}/api/centrex/devices/${deviceId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.delete<void>(url, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.devicesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  getDeviceConfig(
    organizationId: string,
    platform: string,
    deviceAddress: string,
  ): Observable<string> {
    const url = `${this.apiUrl}/api/centrex/devices/${deviceAddress}/config`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http
      .get(url, { headers, responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  notifyDevice(
    organizationId: string,
    platform: string,
    deviceAddress: string,
    deviceAction: string,
  ): Observable<void> {
    const url = `${this.apiUrl}/api/centrex/devices/${deviceAddress}/notify/${deviceAction}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http
      .put<void>(url, null, { headers })
      .pipe(catchError(this.handleError));
  }

  getBlfsTypes(
    organizationId: string,
    platform: string,
    manufacturer: string,
  ): Observable<string[]> {
    const url = `${this.apiUrl}/api/centrex/devices/blfstypes/${manufacturer}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http
      .get<string[]>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  clearCache(): void {
    this.devicesCache.clear();
  }
}
